import { useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import axios, { AxiosError } from 'axios';
import Swal from 'sweetalert2';
import { environment } from '../../../../environment/environment';
import apiResponseHandler from '../../../../utils/apiResponseHandler';
import { trimField, formatServerErrors } from '../utils/csvHelpers';
import { resolveNamesToIds } from '../utils/bulkUploadValidation';
import type { ValidatedRow, BulkCreatePayload, BulkCreateResponse, NamedEntity } from '../types/bulkUpload';

const buildSummary = (created: number, updated: number, failed: number): string =>
  [created && `${created} created`, updated && `${updated} updated`, failed && `${failed} failed`]
    .filter(Boolean)
    .join(', ') || 'No changes';

interface UseBulkSubmitOptions {
  employerId: number | undefined;
  validRows: ValidatedRow[];
  availableRoles: NamedEntity[];
  availableFeatures: NamedEntity[];
  availableBranches: NamedEntity[];
  availableTags: NamedEntity[];
  onError: () => void;
}

interface UseBulkSubmitReturn {
  isSubmitting: boolean;
  handleSubmit: () => Promise<boolean>;
}

const useBulkSubmit = ({
  employerId,
  validRows,
  availableRoles,
  availableFeatures,
  availableBranches,
  availableTags,
  onError,
}: UseBulkSubmitOptions): UseBulkSubmitReturn => {
  const history = useHistory();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submittingRef = useRef(false);

  const buildPayload = (r: ValidatedRow, employer: number): BulkCreatePayload => {
    const emailVal = trimField(r.data, 'email');
    const payload: BulkCreatePayload = {
      username: trimField(r.data, 'username') || emailVal,
      first_name: trimField(r.data, 'first_name'),
      last_name: trimField(r.data, 'last_name'),
      email: emailVal,
      mobile_number: trimField(r.data, 'mobile_number').replace(/\s+/g, ''),
      employer,
      is_active: true,
    };

    const groups = resolveNamesToIds(r.data.roles, availableRoles);
    if (groups?.length) payload.groups = groups;

    const features = resolveNamesToIds(r.data.features, availableFeatures);
    if (features?.length) payload.features = features;

    const branchIds = resolveNamesToIds(r.data.receive_queries_from_branches, availableBranches, true);
    if (branchIds?.length) payload.branch_ids = branchIds;

    const tags = resolveNamesToIds(r.data.tags, availableTags);
    if (tags?.length) payload.tags = tags;

    const emailsEnabledValue = trimField(r.data, 'query_and_leave_approval_emails_enabled');
    if (emailsEnabledValue) {
      payload.receive_email_notifications = emailsEnabledValue.toUpperCase() === 'TRUE';
    }

    return payload;
  };

  const handleSubmit = async (): Promise<boolean> => {
    if (submittingRef.current || !employerId) return false;

    const rowsToSubmit = validRows.map((r) => buildPayload(r, employerId));
    if (rowsToSubmit.length === 0) return false;

    submittingRef.current = true;
    setIsSubmitting(true);

    try {
      const response = await axios.post<BulkCreateResponse>(
        `${environment.baseUrl}/users/bulk_create/`,
        { users: rowsToSubmit },
        { timeout: 120_000 },
      );

      const { created, updated = 0, failed, errors: serverErrors } = response.data;

      if (failed > 0) {
        Swal.fire({
          title: buildSummary(created, updated, failed),
          text: formatServerErrors(serverErrors),
          icon: 'warning',
        });
        onError();
        return false;
      }

      Swal.fire({
        title: `${buildSummary(created, updated, 0)} successfully`,
        icon: 'success',
        toast: true,
        position: 'top',
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });

      history.push('/admin/users');
      return true;
    } catch (error) {
      const axiosError = error as AxiosError<BulkCreateResponse>;
      const responseData = axiosError.response?.data;
      if (responseData?.errors && Array.isArray(responseData.errors)) {
        Swal.fire({
          title: buildSummary(responseData.created || 0, responseData.updated || 0, responseData.failed || 0),
          text: formatServerErrors(responseData.errors),
          icon: 'warning',
        });
      } else {
        apiResponseHandler.handleApiError('Error creating users', error);
      }
      onError();
      return false;
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  return { isSubmitting, handleSubmit };
};

export default useBulkSubmit;
